import { useEffect, useState } from "react";
import { Button, Modal, Radio, Select, Spin } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import {
  fetchProtocolTemplateDropdown,
  type ProtocolTemplateDropdownItem,
} from "../../../pages/protocols/protocolsService";
import { useProtocolsRouter } from "../../custom-hooks/useProtocolsRouter";
import "./CreateProtocolModal.less";

export type CreateProtocolOption = "newProtocol" | "template";

interface CreateProtocolModalProps {
  open: boolean;
  onCancel: () => void;
  onContinue: (
    option: CreateProtocolOption,
    templateId?: string,
    templateName?: string
  ) => void;
}

export const CreateProtocolModal = ({
  open,
  onCancel,
  onContinue,
}: CreateProtocolModalProps): JSX.Element => {
  const { orgId, workspaceId } = useProtocolsRouter();
  const [selectedOption, setSelectedOption] =
    useState<CreateProtocolOption | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );
  const [templates, setTemplates] = useState<ProtocolTemplateDropdownItem[]>(
    []
  );
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      // Fetch templates when modal opens
      void fetchTemplatesData();
    } else {
      // Reset state when modal closes
      setSelectedOption(null);
      setSelectedTemplateId(null);
      setTemplates([]);
      setTemplateError(null);
      setIsLoadingTemplates(false);
    }
  }, [open]);

  const fetchTemplatesData = async (): Promise<void> => {
    if (!orgId || !workspaceId) {
      return;
    }

    setIsLoadingTemplates(true);
    setTemplateError(null);

    try {
      const templatesData = await fetchProtocolTemplateDropdown(
        orgId,
        workspaceId
      );

      setTemplates(templatesData);
    } catch (error) {
      console.error("Error fetching templates:", error);
      setTemplateError("Failed to load templates");
      setTemplates([]);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  const isConfirmDisabled = (): boolean => {
    if (!selectedOption) {
      return true;
    }

    if (selectedOption === "template" && !selectedTemplateId) {
      return true;
    }

    return false;
  };

  const renderTemplateContent = (): JSX.Element => {
    if (isLoadingTemplates) {
      return (
        <Spin
          size="small"
          className="create-protocol-modal__template-loading"
        />
      );
    }

    if (templateError) {
      return (
        <div className="create-protocol-modal__template-error">
          {templateError}
        </div>
      );
    }

    return (
      <Select
        placeholder="Select template"
        value={selectedTemplateId}
        onChange={(value: string) => {
          setSelectedTemplateId(value);
        }}
        options={templates.map((template) => ({
          label: template.name,
          value: template.id,
        }))}
        className="create-protocol-modal__template-dropdown"
      />
    );
  };

  const handleContinue = (): void => {
    if (!isConfirmDisabled()) {
      const selectedTemplate = templates.find(
        (template) => template.id === selectedTemplateId
      );
      onContinue(
        selectedOption as CreateProtocolOption,
        selectedTemplateId ?? undefined,
        selectedTemplate?.name
      );
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      className="create-protocol-modal"
      width={360}
      centered
      closable={false}
      destroyOnHidden
    >
      <div className="create-protocol-modal__container">
        <div className="create-protocol-modal__title">
          <CheckCircleFilled className="create-protocol-modal__title-icon" />
          <span>Create a Protocol</span>
        </div>

        <Radio.Group
          value={selectedOption}
          onChange={(event) => {
            const newOption = event.target.value as CreateProtocolOption;
            setSelectedOption(newOption);
            if (newOption !== "template") {
              setSelectedTemplateId(null);
            }
          }}
          className="create-protocol-modal__options"
        >
          <Radio value="newProtocol">Start a new protocol</Radio>
          <Radio value="template">Use a template</Radio>
        </Radio.Group>

        {selectedOption === "template" && (
          <div className="create-protocol-modal__template-selector">
            {renderTemplateContent()}
          </div>
        )}

        <div className="create-protocol-modal__actions">
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            onClick={handleContinue}
            disabled={isConfirmDisabled()}
            className="create-protocol-modal__continue"
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreateProtocolModal;
